import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { formatMoney } from '@theme/money-formatting';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();
  /** @type {AbortController | null} */
  #quickAddUiAbortController = null;

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#quickAddUiAbortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    if (this.dataset.loading === 'true') return;

    const currentUrl = this.productPageUrl;

    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }

    this.dataset.loading = 'true';
    this.setAttribute('aria-busy', 'true');

    try {
      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (!productGrid) {
        if (currentUrl) window.location.href = currentUrl;
        return;
      }

      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      this.#openQuickAddModal();
    } catch (error) {
      console.warn('[quick-add] Unable to open Quick Add.', error);
      if (currentUrl) window.location.href = currentUrl;
    } finally {
      delete this.dataset.loading;
      this.removeAttribute('aria-busy');
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the quick-add modal.
   * Products using Aurelia's compact offer box do not render Horizon's native
   * variant picker / buy buttons on the product page. For those products we
   * build a purpose-made quick shop from the offer box's variant data.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    const bundleBox = productGrid.querySelector('aurelia-bundle-buy-box');
    const bundleVariants = bundleBox?.querySelector('[data-main-variants]');
    const useAureliaQuickShop = bundleBox instanceof HTMLElement && bundleVariants instanceof HTMLScriptElement;

    modalContent.classList.toggle('quick-add-modal__content--cash', useAureliaQuickShop);
    const modalDialog = modalContent.closest('.quick-add-modal');
    modalDialog?.classList.toggle('quick-add-modal--cash', useAureliaQuickShop);

    if (useAureliaQuickShop) {
      this.#buildAureliaQuickShop(productGrid, bundleBox);
    } else if (isMobileBreakpoint()) {
      // Preserve Horizon's original native quick-add path as a fallback for
      // products/templates that still render a normal variant picker.
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);
      if (productPrice) productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      if (variantPicker) productGrid.appendChild(variantPicker);
      if (productFormComponent) productGrid.appendChild(productFormComponent);
      productDetails?.remove();
    }

    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);

    morph(modalContent, productGrid);

    if (useAureliaQuickShop) {
      this.#bindAureliaQuickShop(modalContent);
    } else {
      this.#quickAddUiAbortController?.abort();
      this.#quickAddUiAbortController = null;
      this.#syncVariantSelection(modalContent);
    }
  }

  /** @param {Element | null} script */
  #readQuickAddJson(script) {
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn('[quick-add] Invalid Aurelia variant data.', error);
      return null;
    }
  }

  /** @param {string | null | undefined} value */
  #largeQuickAddImage(value) {
    if (!value) return '';
    return value.replace(/([?&])width=360(?=(&|$))/, '$1width=900');
  }

  /** @param {number} amount @param {string} moneyFormat @param {string} currency */
  #quickAddMoney(amount, moneyFormat, currency) {
    try {
      return formatMoney(Number(amount) || 0, moneyFormat || '${{amount}}', currency || 'AUD');
    } catch (_error) {
      return `${((Number(amount) || 0) / 100).toFixed(2)} ${currency || ''}`.trim();
    }
  }

  /**
   * Builds a clean fashion quick shop from the existing Aurelia bundle block.
   * @param {Element} productGrid
   * @param {HTMLElement} bundleBox
   */
  #buildAureliaQuickShop(productGrid, bundleBox) {
    const rawVariants = this.#readQuickAddJson(bundleBox.querySelector('[data-main-variants]'));
    const variants = Array.isArray(rawVariants) ? rawVariants : [];
    if (!variants.length) return;

    const currentVariantData = this.#readQuickAddJson(bundleBox.querySelector('[data-current-variant]'));
    const currentVariantId = Number(currentVariantData?.id || 0);
    const currentVariant = variants.find((variant) => Number(variant.id) === currentVariantId) || variants.find((variant) => variant.available) || variants[0];

    const moneyFormat = bundleBox.dataset.moneyFormat || '${{amount}}';
    const currency = bundleBox.dataset.currency || 'AUD';
    const locale = bundleBox.dataset.locale || 'en-AU';
    const freeShippingThreshold = Number(bundleBox.dataset.freeShippingThreshold || 0);
    const freeShippingHeading = bundleBox.dataset.freeShippingHeading || '';
    const returnsLabel = bundleBox.dataset.returnsLabel || '30-day returns';
    const secureCheckoutLabel = bundleBox.dataset.secureCheckoutLabel || 'Secure checkout';
    const productId = bundleBox.dataset.productId || this.dataset.productId || '';
    const sectionId = bundleBox.dataset.sectionId || '';

    const optionSelects = Array.from(bundleBox.querySelectorAll('[data-item-options="1"] select[data-bundle-option]'));
    const optionDefinitions = optionSelects.map((select, index) => {
      const values = Array.from(select.options).map((option) => ({
        value: option.value,
        label: option.dataset.displayValue || option.textContent?.replace(/\s+—\s+Sold out\s*$/i, '').trim() || option.value,
        initiallyUnavailable: option.disabled,
      }));

      return {
        index: Number(select.dataset.optionIndex ?? index),
        name: select.dataset.optionName || `Option ${index + 1}`,
        type: select.dataset.optionType || 'option',
        values,
      };
    });

    const initialSelections = optionDefinitions.map((definition) => {
      const variantValue = currentVariant?.options?.[definition.index] ?? definition.values[0]?.value ?? null;
      if (definition.type === 'size' && definition.values.length > 1) return null;
      return variantValue;
    });

    const doc = productGrid.ownerDocument || document;
    const shell = doc.createElement('div');
    shell.className = 'quick-add-cash';
    shell.dataset.quickAddCash = 'true';
    shell.dataset.moneyFormat = moneyFormat;
    shell.dataset.currency = currency;
    shell.dataset.locale = locale;
    shell.dataset.productId = productId;
    shell.dataset.sectionId = sectionId;
    shell.dataset.freeShippingThreshold = String(freeShippingThreshold);
    shell.dataset.freeShippingHeading = freeShippingHeading;
    shell.dataset.returnsLabel = returnsLabel;
    shell.dataset.secureCheckoutLabel = secureCheckoutLabel;
    shell.dataset.selectedOptions = JSON.stringify(initialSelections);

    const variantsScript = doc.createElement('script');
    variantsScript.type = 'application/json';
    variantsScript.dataset.quickAddVariants = 'true';
    variantsScript.textContent = JSON.stringify(variants);
    shell.appendChild(variantsScript);

    const media = doc.createElement('div');
    media.className = 'quick-add-cash__media';

    const mediaInner = doc.createElement('div');
    mediaInner.className = 'quick-add-cash__media-inner';

    const image = doc.createElement('img');
    image.className = 'quick-add-cash__image';
    image.dataset.qaImage = 'true';
    image.src = this.#largeQuickAddImage(currentVariant?.image) || '';
    image.alt = this.dataset.productTitle || bundleBox.dataset.productTitle || '';
    image.width = 900;
    image.height = 1125;
    image.loading = 'eager';
    mediaInner.appendChild(image);

    if ((this.dataset.productTitle || '').toUpperCase().includes('UPF50')) {
      const protectionBadge = doc.createElement('span');
      protectionBadge.className = 'quick-add-cash__media-badge';
      protectionBadge.textContent = 'UPF50+ protection';
      mediaInner.appendChild(protectionBadge);
    }

    media.appendChild(mediaInner);

    const panel = doc.createElement('div');
    panel.className = 'quick-add-cash__panel';

    const header = doc.createElement('div');
    header.className = 'quick-add-cash__header';

    const eyebrow = doc.createElement('span');
    eyebrow.className = 'quick-add-cash__eyebrow';
    eyebrow.textContent = 'Quick shop';
    header.appendChild(eyebrow);

    const title = doc.createElement('a');
    title.className = 'quick-add-cash__title';
    title.href = this.productPageUrl;
    title.textContent = this.dataset.productTitle || bundleBox.dataset.productTitle || '';
    header.appendChild(title);

    const priceRow = doc.createElement('div');
    priceRow.className = 'quick-add-cash__price-row';

    const price = doc.createElement('strong');
    price.className = 'quick-add-cash__price';
    price.dataset.qaPrice = 'true';
    price.textContent = this.#quickAddMoney(currentVariant?.price || 0, moneyFormat, currency);
    priceRow.appendChild(price);

    const comparePrice = doc.createElement('span');
    comparePrice.className = 'quick-add-cash__compare-price';
    comparePrice.dataset.qaComparePrice = 'true';
    if (Number(currentVariant?.compareAtPrice || 0) > Number(currentVariant?.price || 0)) {
      comparePrice.textContent = this.#quickAddMoney(currentVariant.compareAtPrice, moneyFormat, currency);
    } else {
      comparePrice.hidden = true;
    }
    priceRow.appendChild(comparePrice);
    header.appendChild(priceRow);

    const availability = doc.createElement('div');
    availability.className = 'quick-add-cash__availability';
    availability.dataset.qaAvailability = 'true';
    const availabilityDot = doc.createElement('span');
    availabilityDot.className = 'quick-add-cash__availability-dot';
    availabilityDot.setAttribute('aria-hidden', 'true');
    const availabilityText = doc.createElement('span');
    availabilityText.dataset.qaAvailabilityText = 'true';
    availabilityText.textContent = currentVariant?.available ? 'Available to order' : 'Unavailable';
    availability.append(availabilityDot, availabilityText);
    header.appendChild(availability);

    panel.appendChild(header);

    const divider = doc.createElement('div');
    divider.className = 'quick-add-cash__divider';
    panel.appendChild(divider);

    const optionsArea = doc.createElement('div');
    optionsArea.className = 'quick-add-cash__options';

    for (const definition of optionDefinitions) {
      const group = doc.createElement('fieldset');
      group.className = `quick-add-cash__option quick-add-cash__option--${definition.type}`;
      group.dataset.qaOptionIndex = String(definition.index);
      group.dataset.qaOptionType = definition.type;

      const legend = doc.createElement('legend');
      legend.className = 'quick-add-cash__legend';

      const legendLabel = doc.createElement('span');
      legendLabel.className = 'quick-add-cash__legend-label';
      legendLabel.textContent = definition.name;
      legend.appendChild(legendLabel);

      const selectedLabel = doc.createElement('span');
      selectedLabel.className = 'quick-add-cash__selected-value';
      selectedLabel.dataset.qaSelectedValue = 'true';
      const initial = initialSelections[definition.index];
      const initialDefinition = definition.values.find((value) => value.value === initial);
      selectedLabel.textContent = initialDefinition?.label || (definition.type === 'size' ? 'Select' : '');
      legend.appendChild(selectedLabel);

      if (definition.type === 'size') {
        const sizeGuide = doc.createElement('a');
        sizeGuide.className = 'quick-add-cash__size-guide';
        sizeGuide.href = '/pages/size-guide';
        sizeGuide.textContent = 'Size guide';
        legend.appendChild(sizeGuide);
      }

      group.appendChild(legend);

      const choices = doc.createElement('div');
      choices.className = `quick-add-cash__choices quick-add-cash__choices--${definition.type}`;

      for (const valueDefinition of definition.values) {
        const choice = doc.createElement('button');
        choice.type = 'button';
        choice.className = `quick-add-cash__choice quick-add-cash__choice--${definition.type}`;
        choice.dataset.qaChoice = 'true';
        choice.dataset.qaOptionIndex = String(definition.index);
        choice.dataset.qaValue = valueDefinition.value;
        choice.dataset.qaLabel = valueDefinition.label;
        choice.setAttribute('aria-pressed', initialSelections[definition.index] === valueDefinition.value ? 'true' : 'false');
        choice.setAttribute('aria-label', `${definition.name}: ${valueDefinition.label}`);

        if (definition.type === 'colour') {
          const swatch = doc.createElement('span');
          swatch.className = 'quick-add-cash__colour-swatch';
          const swatchVariant = variants.find((variant) => variant.options?.[definition.index] === valueDefinition.value && variant.image);
          if (swatchVariant?.image) {
            const swatchImage = doc.createElement('img');
            swatchImage.src = this.#largeQuickAddImage(swatchVariant.image);
            swatchImage.alt = '';
            swatchImage.width = 56;
            swatchImage.height = 70;
            swatch.appendChild(swatchImage);
          } else {
            swatch.textContent = valueDefinition.label.slice(0, 1);
          }
          choice.appendChild(swatch);
        } else {
          const choiceText = doc.createElement('span');
          choiceText.textContent = valueDefinition.label;
          choice.appendChild(choiceText);
        }

        choices.appendChild(choice);
      }

      group.appendChild(choices);
      optionsArea.appendChild(group);
    }

    panel.appendChild(optionsArea);

    const footer = doc.createElement('div');
    footer.className = 'quick-add-cash__footer';

    const status = doc.createElement('p');
    status.className = 'quick-add-cash__status';
    status.dataset.qaStatus = 'true';
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    footer.appendChild(status);

    const addButton = doc.createElement('button');
    addButton.type = 'button';
    addButton.className = 'quick-add-cash__add';
    addButton.dataset.qaAdd = 'true';
    addButton.disabled = true;

    const addLabel = doc.createElement('span');
    addLabel.dataset.qaAddLabel = 'true';
    addLabel.textContent = optionDefinitions.some((definition) => definition.type === 'size' && definition.values.length > 1)
      ? 'Select a size'
      : 'Add to bag';
    addButton.appendChild(addLabel);

    const addDivider = doc.createElement('span');
    addDivider.className = 'quick-add-cash__add-divider';
    addDivider.textContent = '·';
    addButton.appendChild(addDivider);

    const addPrice = doc.createElement('span');
    addPrice.dataset.qaAddPrice = 'true';
    addPrice.textContent = this.#quickAddMoney(currentVariant?.price || 0, moneyFormat, currency);
    addButton.appendChild(addPrice);

    const spinner = doc.createElement('span');
    spinner.className = 'quick-add-cash__spinner';
    spinner.setAttribute('aria-hidden', 'true');
    addButton.appendChild(spinner);

    footer.appendChild(addButton);

    const trust = doc.createElement('div');
    trust.className = 'quick-add-cash__trust';

    if (freeShippingHeading) {
      const shipping = doc.createElement('span');
      shipping.textContent = freeShippingHeading;
      trust.appendChild(shipping);
    }

    const returns = doc.createElement('span');
    returns.textContent = returnsLabel;
    trust.appendChild(returns);

    const secure = doc.createElement('span');
    secure.textContent = secureCheckoutLabel;
    trust.appendChild(secure);

    footer.appendChild(trust);

    const productLink = doc.createElement('a');
    productLink.className = 'quick-add-cash__details-link';
    productLink.href = this.productPageUrl;
    productLink.textContent = 'View full product details';
    footer.appendChild(productLink);

    panel.appendChild(footer);
    shell.append(media, panel);
    productGrid.replaceChildren(shell);
  }

  /** @param {Element} modalContent */
  #bindAureliaQuickShop(modalContent) {
    this.#quickAddUiAbortController?.abort();
    this.#quickAddUiAbortController = new AbortController();
    const { signal } = this.#quickAddUiAbortController;

    const root = modalContent.querySelector('[data-quick-add-cash]');
    if (!(root instanceof HTMLElement)) return;

    const rawVariants = this.#readQuickAddJson(root.querySelector('[data-quick-add-variants]'));
    const variants = Array.isArray(rawVariants) ? rawVariants : [];
    if (!variants.length) return;

    let selections = [];
    try {
      selections = JSON.parse(root.dataset.selectedOptions || '[]');
    } catch (_error) {
      selections = [];
    }

    const groups = Array.from(root.querySelectorAll('[data-qa-option-index]')).filter(
      (element) => element instanceof HTMLFieldSetElement
    );
    const sizeGroup = groups.find((group) => group.dataset.qaOptionType === 'size');
    const sizeIndex = sizeGroup ? Number(sizeGroup.dataset.qaOptionIndex) : -1;

    const matchesSelected = (variant, exceptIndex = -1, replacementValue = null) => {
      return selections.every((selected, index) => {
        const value = index === exceptIndex ? replacementValue : selected;
        return value == null || value === '' || variant.options?.[index] === value;
      });
    };

    const updateUi = () => {
      for (const group of groups) {
        const index = Number(group.dataset.qaOptionIndex);
        const selectedValue = selections[index] ?? null;
        const selectedLabel = group.querySelector('[data-qa-selected-value]');
        const selectedChoice = group.querySelector(`[data-qa-choice][data-qa-value="${CSS.escape(String(selectedValue ?? ''))}"]`);
        if (selectedLabel instanceof HTMLElement) {
          selectedLabel.textContent = selectedChoice instanceof HTMLElement
            ? selectedChoice.dataset.qaLabel || selectedValue || ''
            : group.dataset.qaOptionType === 'size'
              ? 'Select'
              : '';
        }

        for (const choice of group.querySelectorAll('[data-qa-choice]')) {
          if (!(choice instanceof HTMLButtonElement)) continue;
          const value = choice.dataset.qaValue || '';
          const selected = selections[index] === value;
          choice.setAttribute('aria-pressed', selected ? 'true' : 'false');

          const available = variants.some((variant) => {
            if (!variant.available || variant.options?.[index] !== value) return false;
            return selections.every((otherSelection, otherIndex) => {
              if (otherIndex === index || otherSelection == null || otherSelection === '') return true;
              return variant.options?.[otherIndex] === otherSelection;
            });
          });
          choice.disabled = !available && !selected;
          choice.classList.toggle('is-unavailable', !available);
        }
      }

      const allSelected = groups.every((group) => {
        const index = Number(group.dataset.qaOptionIndex);
        return selections[index] != null && selections[index] !== '';
      });

      const exactVariant = groups.length === 0
        ? variants.find((variant) => variant.available) || variants[0]
        : allSelected
          ? variants.find((variant) => variant.options?.every((value, index) => selections[index] === value))
          : null;
      const previewVariant = exactVariant || variants.find((variant) => variant.available && matchesSelected(variant)) || variants.find((variant) => matchesSelected(variant)) || variants[0];

      const moneyFormat = root.dataset.moneyFormat || '${{amount}}';
      const currency = root.dataset.currency || 'AUD';
      const price = root.querySelector('[data-qa-price]');
      const comparePrice = root.querySelector('[data-qa-compare-price]');
      const addPrice = root.querySelector('[data-qa-add-price]');
      const image = root.querySelector('[data-qa-image]');
      const availability = root.querySelector('[data-qa-availability]');
      const availabilityText = root.querySelector('[data-qa-availability-text]');
      const addButton = root.querySelector('[data-qa-add]');
      const addLabel = root.querySelector('[data-qa-add-label]');
      const status = root.querySelector('[data-qa-status]');

      if (previewVariant) {
        const formattedPrice = this.#quickAddMoney(previewVariant.price || 0, moneyFormat, currency);
        if (price instanceof HTMLElement) price.textContent = formattedPrice;
        if (addPrice instanceof HTMLElement) addPrice.textContent = formattedPrice;

        if (comparePrice instanceof HTMLElement) {
          const compare = Number(previewVariant.compareAtPrice || 0);
          const sale = Number(previewVariant.price || 0);
          if (compare > sale) {
            comparePrice.hidden = false;
            comparePrice.textContent = this.#quickAddMoney(compare, moneyFormat, currency);
          } else {
            comparePrice.hidden = true;
            comparePrice.textContent = '';
          }
        }

        if (image instanceof HTMLImageElement && previewVariant.image) {
          const nextSrc = this.#largeQuickAddImage(previewVariant.image);
          if (image.src !== nextSrc) image.src = nextSrc;
        }
      }

      if (availability instanceof HTMLElement) {
        availability.classList.toggle('is-unavailable', Boolean(allSelected && !exactVariant?.available));
      }
      if (availabilityText instanceof HTMLElement) {
        availabilityText.textContent = allSelected
          ? exactVariant?.available
            ? 'Available to order'
            : 'This option is sold out'
          : 'Available to order';
      }

      if (!(addButton instanceof HTMLButtonElement) || !(addLabel instanceof HTMLElement)) return;
      root.dataset.currentVariantId = exactVariant?.id ? String(exactVariant.id) : '';

      if (!allSelected) {
        addButton.disabled = true;
        if (sizeIndex >= 0 && (selections[sizeIndex] == null || selections[sizeIndex] === '')) {
          addLabel.textContent = 'Select a size';
          if (status instanceof HTMLElement) status.textContent = 'Choose your size to add this item.';
        } else {
          const missingGroup = groups.find((group) => {
            const index = Number(group.dataset.qaOptionIndex);
            return selections[index] == null || selections[index] === '';
          });
          addLabel.textContent = `Select ${missingGroup?.querySelector('.quick-add-cash__legend-label')?.textContent?.toLowerCase() || 'option'}`;
          if (status instanceof HTMLElement) status.textContent = 'Choose your options to continue.';
        }
        return;
      }

      if (!exactVariant?.available) {
        addButton.disabled = true;
        addLabel.textContent = 'Sold out';
        if (status instanceof HTMLElement) status.textContent = 'That combination is currently unavailable.';
        return;
      }

      addButton.disabled = false;
      addLabel.textContent = 'Add to bag';
      if (status instanceof HTMLElement) status.textContent = '';
    };

    root.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const choice = target.closest('[data-qa-choice]');
        if (choice instanceof HTMLButtonElement) {
          const index = Number(choice.dataset.qaOptionIndex);
          const value = choice.dataset.qaValue || '';
          selections[index] = value;

          // When changing colour/another option invalidates the chosen size,
          // clear size instead of leaving the customer on a dead combination.
          if (sizeIndex >= 0 && index !== sizeIndex && selections[sizeIndex]) {
            const compatible = variants.some((variant) => variant.available && matchesSelected(variant));
            if (!compatible) selections[sizeIndex] = null;
          }

          root.dataset.selectedOptions = JSON.stringify(selections);
          updateUi();
          return;
        }

        const addButton = target.closest('[data-qa-add]');
        if (addButton instanceof HTMLButtonElement && !addButton.disabled) {
          const variantId = Number(root.dataset.currentVariantId || 0);
          const variant = variants.find((candidate) => Number(candidate.id) === variantId);
          if (variant?.available) this.#addAureliaQuickShopVariant(root, variant);
        }
      },
      { signal }
    );

    updateUi();
  }

  /** @param {HTMLElement} root @param {any} variant */
  async #addAureliaQuickShopVariant(root, variant) {
    const button = root.querySelector('[data-qa-add]');
    const label = root.querySelector('[data-qa-add-label]');
    const status = root.querySelector('[data-qa-status]');
    if (!(button instanceof HTMLButtonElement) || root.dataset.loading === 'true') return;

    root.dataset.loading = 'true';
    button.disabled = true;
    if (label instanceof HTMLElement) label.textContent = 'Adding…';
    if (status instanceof HTMLElement) status.textContent = '';

    const cartComponents = Array.from(document.querySelectorAll('cart-items-component'));
    const sectionIds = cartComponents
      .map((component) => component instanceof HTMLElement ? component.dataset.sectionId : '')
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    root.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
      promise: deferred.promise,
    }));

    try {
      const rootPath = window.Shopify?.routes?.root || '/';
      const addUrl = window.Theme?.routes?.cart_add_url || `${rootPath}cart/add.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [{ id: Number(variant.id), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });

      const result = await response.json();
      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Could not add this item.');
      }

      const cart = await this.#fetchQuickAddCartData();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'aurelia-quick-add',
          sourceId: root.dataset.productId || '',
          itemCount: 1,
          productId: root.dataset.productId || '',
          sections: result.sections,
          didError: false,
        },
      });

      if (label instanceof HTMLElement) label.textContent = 'Added ✓';
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Something went wrong. Please try again.';
      root.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      deferred.resolve({
        cart: null,
        userErrors: [{ message }],
        detail: { didError: true, source: 'aurelia-quick-add', productId: root.dataset.productId || '' },
      });
      if (status instanceof HTMLElement) status.textContent = message;
      if (label instanceof HTMLElement) label.textContent = 'Add to bag';
      delete root.dataset.loading;
      button.disabled = false;
    }
  }

  async #fetchQuickAddCartData() {
    const cartComponent = document.querySelector('cart-items-component');
    if (cartComponent) {
      await customElements.whenDefined('cart-items-component');
      if (typeof cartComponent.fetchCartData === 'function') return cartComponent.fetchCartData();
    }

    const rootPath = window.Shopify?.routes?.root || '/';
    const cartPath = window.Theme?.routes?.cart_url || `${rootPath}cart`;
    const response = await fetch(`${cartPath}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
